<template>
	<div id="app">
		<div class="alert" v-if="ie">
			<p>
				You are using an
				<b>outdated</b> browser. Please <a href="https://browsehappy.com/">upgrade your browser</a> to improve your experience and security.
			</p>
		</div>
		<Loader v-show="loading" />
		<div v-if="settings">
			<MainNav ref="navComponent" :settings="settings" />
			<main>
				<router-view />
			</main>
		</div>
	</div>
</template>

<script>
import { ref, onBeforeMount, onMounted, defineAsyncComponent } from 'vue';
import axios from "axios";

export default {
	name: "app",
	components: {
		Loader: defineAsyncComponent(() => import("@/components/Loader.vue")),
		MainNav: defineAsyncComponent(() => import("@/components/MainNav.vue")),
	},
	setup() {
		const settings = ref(null);
		const loading = ref(true);
		const ie = ref(false);
		const navComponent = ref(null);
		const canvaOffset = ref(false);

		const isIE = () => {
			const ua = navigator.userAgent;
			/* MSIE used to detect old browsers and Trident used to newer ones*/
			return ua.indexOf("MSIE ") > -1 || ua.indexOf("Trident/") > -1;
		};

		onBeforeMount(() => {
			ie.value = isIE();
		});

		onMounted(() => {
			axios
				.get("/_data/settings.json")
				.then((response) => {
					settings.value = response.data;
					loading.value = false;
				})
				.catch((error) => {
					alert(error);
				});
		});

		const onToggleCanva = (menuVisible) => {
			canvaOffset.value = menuVisible;
		};

		return {
			settings,
			loading,
			ie,
			navComponent,
			onToggleCanva,
		};
	},
};
</script>

<style>
* {
	box-sizing: border-box;
}
html,
body {
	font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, "Apple Color Emoji", Arial, sans-serif, "Segoe UI Emoji", "Segoe UI Symbol";
	background-color: var(--main-bg-color);
	color: var(--main-txt-color);
	font-size: 18px;
}
html:focus,
body:focus {
	outline-width: 0 !important;
}
a,
a:hover,
a:active,
a:focus {
	color: var(--main-txt-color);
}
a:hover {
	text-decoration: none;
}
a:focus,
button:focus,
[tabindex="-1"]:focus {
	outline-color: rgb(255, 0, 170);
	outline-style: dashed;
	outline-width: 1px;
}
.wrapper {
	margin: 0 auto;
	padding: 0 24px;
	max-width: 992px;
}
.sr-only {
	border: 0;
	clip: rect(0, 0, 0, 0);
	height: 1px;
	margin: -1px;
	overflow: hidden;
	padding: 0;
	position: absolute;
	width: 1px;
}
.highlight {
	background-color: var(--main-highlight);
	white-space: nowrap;
}
.greeting {
	align-self: flex-end;
}
.light {
	margin-left: 8px;
	display: flex;
	align-items: flex-end;
}
h1 span.underline {
	position: relative;
}
ul {
	list-style-type: square;
}
.content li {
	margin: 8px;
}
.dynamic-content li {
	margin: 24px 0 0 0;
}
.icon-btn {
	background: transparent;
	color: var(--main-charcoal);
	border-radius: 1.5em;
	font-size: 1.2em;
	width: 42px;
	height: 42px;
	display: flex;
	align-items: center;
	justify-content: center;
	transition: all 0.2s ease;
}
.icon-btn:hover,
.icon-btn:focus {
	background: var(--main-gray-tras);
	transform: scale(1.2);
}
.btn {
	background: transparent;
	text-decoration: none;
	text-align: center;
	cursor: pointer;
	color: var(--main-charcoal);
	padding: 12px 16px;
	border: 1px solid var(--main-charcoal);
	border-radius: 1.5em;
	display: block;
	transition: all 0.15s ease;
}
.btn:hover,
.btn:focus {
	color: var(--main-bg-color);
	background: var(--main-charcoal);
}
@media (max-width: 640px) {
	.btn {
		padding: 16px 16px;
	}
}
.btn-small {
	padding: 4px 12px;
	font-size: 0.9em;
}
h1 {
	position: relative;
	margin-bottom: 32px;
}
.wrapper h1 {
	margin-top: 0;
}
.wrapper h1 span {
	display: inline-block;
	padding-top: 100px;
}
p {
	line-height: 1.5em;
}
.fade-enter-active,
.fade-leave-active {
	transition: opacity 0.2s ease;
}
.fade-enter,
.fade-leave-to {
	opacity: 0;
}

/* TOOLTIP */
.tooltip {
	display: block !important;
	z-index: 25;
	font-size: 0.8em;
	line-height: 1em;
}
.tooltip .tooltip-inner {
	background: var(--main-charcoal);
	color: var(--main-bg-color);
	border-radius: 16px;
	padding: 4px 10px 6px;
}
.tooltip .tooltip-arrow {
	width: 0;
	height: 0;
	border-style: solid;
	position: absolute;
	margin: 5px;
	border-color: black;
	z-index: 1;
}
.tooltip[x-placement^="top"] {
	margin-bottom: 5px;
}
.tooltip[x-placement^="top"] .tooltip-arrow {
	border-width: 5px 5px 0 5px;
	border-left-color: transparent !important;
	border-right-color: transparent !important;
	border-bottom-color: transparent !important;
	bottom: -5px;
	left: calc(50% - 5px);
	margin-top: 0;
	margin-bottom: 0;
}
.tooltip[x-placement^="bottom"] {
	margin-top: 5px;
}
.tooltip[x-placement^="bottom"] .tooltip-arrow {
	border-width: 0 5px 5px 5px;
	border-left-color: transparent !important;
	border-right-color: transparent !important;
	border-top-color: transparent !important;
	top: -5px;
	left: calc(50% - 5px);
	margin-top: 0;
	margin-bottom: 0;
}
.tooltip[x-placement^="right"] {
	margin-left: 5px;
}
.tooltip[x-placement^="right"] .tooltip-arrow {
	border-width: 5px 5px 5px 0;
	border-left-color: transparent !important;
	border-top-color: transparent !important;
	border-bottom-color: transparent !important;
	left: -5px;
	top: calc(50% - 5px);
	margin-left: 0;
	margin-right: 0;
}
.tooltip[x-placement^="left"] {
	margin-right: 5px;
}
.tooltip[x-placement^="left"] .tooltip-arrow {
	border-width: 5px 0 5px 5px;
	border-top-color: transparent !important;
	border-right-color: transparent !important;
	border-bottom-color: transparent !important;
	right: -5px;
	top: calc(50% - 5px);
	margin-left: 0;
	margin-right: 0;
}
.tooltip.popover .popover-inner {
	background: #f9f9f9;
	color: black;
	padding: 24px;
	border-radius: 4px;
	box-shadow: 0 5px 30px rgba(0, 0, 0, 0.1);
}
.tooltip.popover .popover-arrow {
	border-color: #f9f9f9;
}
.tooltip[aria-hidden="true"] {
	visibility: hidden;
	opacity: 0;
	transition: opacity 0.15s, visibility 0.15s;
}
.tooltip[aria-hidden="false"] {
	visibility: visible;
	opacity: 1;
	transition: opacity 0.15s;
}
.progressive-background-image {
	background-position: 0 30% !important;
	border-radius: 4px;
}
</style>
